import { useMemo, useState } from 'react';
import { formatDate, formatDateTime } from './devisUtils';
import '../assets/styles/PlanningFormateur.css';

const JOURS_SEMAINE = ['lun', 'mar', 'mer', 'jeu', 'ven', 'sam', 'dim'];

const pad = (n) => String(n).padStart(2, '0');
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const jourDe = (texte) => {
  const [a, m, j] = String(texte).slice(0, 10).split('-').map(Number);
  return new Date(a, m - 1, j);
};

/** Ce que le formateur a signé, journée par journée (même lecture que l'onglet Formations). */
function etatJournee(jour, emargements, aujourdhui) {
  const signee = (emargements || []).find((e) => e.jour === jour);
  if (signee) {
    return {
      cle: 'signee',
      libelle: `signée le ${formatDateTime(signee.signeLe)}`,
      court: 'signée',
    };
  }
  if (jour <= aujourdhui) return { cle: 'asigner', libelle: 'à signer', court: 'à signer' };
  return { cle: 'avenir', libelle: 'à venir', court: 'à venir' };
}

/**
 * Planning du formateur : les formations qu'il doit tenir, sur un calendrier
 * mensuel. Chaque journée de chaque séance y figure, avec l'état de son
 * émargement ; un clic sur un jour en donne le détail (société, lieu,
 * horaires), et la liste des prochaines journées suit le calendrier.
 */
function PlanningFormateur({ sessions = [] }) {
  const aujourdhui = iso(new Date());

  // Toutes les journées de toutes les séances, rangées par date.
  const parJour = useMemo(() => {
    const carte = new Map();
    for (const s of sessions) {
      for (const jour of s.jours || []) {
        if (!carte.has(jour)) carte.set(jour, []);
        carte.get(jour).push({ session: s, etat: etatJournee(jour, s.emargements, aujourdhui) });
      }
    }
    for (const liste of carte.values()) {
      liste.sort((a, b) =>
        String(a.session.heureDebut || '').localeCompare(String(b.session.heureDebut || ''))
      );
    }
    return carte;
  }, [sessions, aujourdhui]);

  const dates = useMemo(() => [...parJour.keys()].sort(), [parJour]);

  // Premier affichage : le mois de la prochaine journée, à défaut du mois en cours.
  const depart = useMemo(() => {
    const cible = dates.find((d) => d >= aujourdhui) || dates[dates.length - 1] || aujourdhui;
    const d = jourDe(cible);
    return { annee: d.getFullYear(), mois: d.getMonth() };
  }, [dates, aujourdhui]);

  const [vue, setVue] = useState(depart);
  const [jourChoisi, setJourChoisi] = useState(null);

  const premier = new Date(vue.annee, vue.mois, 1);
  const decalage = (premier.getDay() + 6) % 7; // lundi en premier
  const cases = Array.from({ length: 42 }, (_, i) => {
    const d = new Date(vue.annee, vue.mois, 1 - decalage + i);
    return { date: d, iso: iso(d), dansLeMois: d.getMonth() === vue.mois };
  });
  const derniereLigneVide = cases.slice(35).every((c) => !c.dansLeMois);
  const casesAffichees = derniereLigneVide ? cases.slice(0, 35) : cases;

  const titreMois = premier.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });

  function changerMois(delta) {
    const d = new Date(vue.annee, vue.mois + delta, 1);
    setVue({ annee: d.getFullYear(), mois: d.getMonth() });
  }

  function allerAujourdhui() {
    const d = new Date();
    setVue({ annee: d.getFullYear(), mois: d.getMonth() });
    setJourChoisi(iso(d));
  }

  const aVenir = dates.filter((d) => d >= aujourdhui).slice(0, 12);
  const detail = jourChoisi ? parJour.get(jourChoisi) || [] : [];

  if (!sessions.length) {
    return (
      <div className="devis-card clients-vide">
        Aucune formation planifiée pour l’instant : vos prochaines journées apparaîtront ici dès
        qu’elles vous seront assignées.
      </div>
    );
  }

  return (
    <div className="planning">
      {/* Deux colonnes : le calendrier d'un côté, le détail du jour et les
          prochaines journées de l'autre (empilées sur petit écran). */}
      <div className="planning-colonne planning-colonne-calendrier">
      <div className="devis-card planning-carte">
        <div className="planning-tete">
          <div className="planning-titre">{titreMois}</div>
          <div className="planning-nav">
            <button type="button" className="devis-btn devis-btn-sm" onClick={() => changerMois(-1)} aria-label="Mois précédent">
              ‹
            </button>
            <button type="button" className="devis-btn devis-btn-sm" onClick={allerAujourdhui}>
              Aujourd’hui
            </button>
            <button type="button" className="devis-btn devis-btn-sm" onClick={() => changerMois(1)} aria-label="Mois suivant">
              ›
            </button>
          </div>
        </div>

        <div className="planning-legende">
          <span className="planning-puce planning-puce-avenir">À venir</span>
          <span className="planning-puce planning-puce-asigner">À signer</span>
          <span className="planning-puce planning-puce-signee">Signée</span>
        </div>

        <div className="planning-grille" role="grid">
          {JOURS_SEMAINE.map((j) => (
            <div key={j} className="planning-entete" role="columnheader">
              {j}
            </div>
          ))}
          {casesAffichees.map((c) => {
            const evenements = parJour.get(c.iso) || [];
            const choisi = jourChoisi === c.iso;
            return (
              <div
                key={c.iso}
                role="gridcell"
                className={
                  'planning-jour' +
                  (c.dansLeMois ? '' : ' planning-jour-hors') +
                  (c.iso === aujourdhui ? ' planning-jour-aujourdhui' : '') +
                  (evenements.length ? ' planning-jour-plein' : '') +
                  (choisi ? ' planning-jour-choisi' : '')
                }
                onClick={() => setJourChoisi(evenements.length ? (choisi ? null : c.iso) : null)}
              >
                <span className="planning-numero">{c.date.getDate()}</span>
                {evenements.slice(0, 3).map(({ session, etat }) => (
                  <span
                    key={`${session.id}-${c.iso}`}
                    className={`planning-evenement planning-evenement-${etat.cle}`}
                    title={`${session.intitule}${session.societeNom ? ` — ${session.societeNom}` : ''} (${etat.court})`}
                  >
                    {session.heureDebut ? `${session.heureDebut} ` : ''}
                    {session.intitule}
                  </span>
                ))}
                {evenements.length > 3 && (
                  <span className="planning-plus">+{evenements.length - 3}</span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      </div>

      <div className="planning-colonne planning-colonne-liste">
      {jourChoisi && detail.length > 0 && (
        <div className="devis-card clients-demande">
          <div className="clients-section">
            <div className="clients-section-titre">{formatDate(jourChoisi)}</div>
            {detail.map(({ session, etat }) => (
              <div className="clients-ligne" key={session.id}>
                <div className="clients-ligne-texte">
                  <div>
                    {session.intitule}
                    {session.groupeLibelle ? ` — ${session.groupeLibelle}` : ''}
                  </div>
                  <div className="clients-ligne-detail">
                    {[
                      session.societeNom,
                      session.heureDebut && session.heureFin
                        ? `${session.heureDebut}–${session.heureFin}`
                        : null,
                      session.lieu || session.modalite,
                      session.adresse,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </div>
                </div>
                <span
                  className={`devis-status-pill${
                    etat.cle === 'signee' ? ' devis-pill-valide' : etat.cle === 'asigner' ? ' devis-pill-attente' : ''
                  }`}
                >
                  {etat.libelle}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="devis-card clients-demande">
        <div className="clients-section">
          <div className="clients-section-titre">Prochaines journées</div>
          {aVenir.length === 0 && (
            <div className="clients-ligne-detail">Aucune journée à venir.</div>
          )}
          {aVenir.map((jour) =>
            parJour.get(jour).map(({ session, etat }) => (
              <div className="clients-ligne" key={`${session.id}-${jour}`}>
                <div className="clients-ligne-texte">
                  <div>{formatDate(jour)}</div>
                  <div className="clients-ligne-detail">
                    {[
                      session.intitule,
                      session.societeNom,
                      session.heureDebut && session.heureFin
                        ? `${session.heureDebut}–${session.heureFin}`
                        : null,
                      session.lieu || session.modalite,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </div>
                </div>
                <span
                  className={`devis-status-pill${
                    etat.cle === 'signee' ? ' devis-pill-valide' : etat.cle === 'asigner' ? ' devis-pill-attente' : ''
                  }`}
                >
                  {etat.libelle}
                </span>
              </div>
            ))
          )}
        </div>
      </div>
      </div>
    </div>
  );
}

export default PlanningFormateur;
